import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import ConsentBanner from "./components/ConsentBanner";
import { toast } from "sonner";
import OfflineIndicator from "./components/ui/OfflineIndicator";

// Pages
import LandingPage from "./pages/LandingPage";
import BookingPage from "./pages/BookingPage";
import BookingSuccessPage from "./pages/BookingSuccessPage";
import PayBalancePage from "./pages/PayBalancePage";
import PayDepositPage from "./pages/PayDepositPage";
import BalanceSuccessPage from "./pages/BalanceSuccessPage";
import InvoiceViewPage from "./pages/InvoiceViewPage";
import DashboardPage from "./pages/DashboardPage";
import AuthCallback from "./pages/AuthCallback";
import TermsPage from "./pages/TermsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import PromotionTermsPage from "./pages/PromotionTermsPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import { getSessionToken, setSessionToken } from "./lib/sessionToken";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Session-token fallback for browsers where the HttpOnly cookie doesn't
// stick (e.g. some iOS in-app webviews). Held in memory only — never
// localStorage (SEC-N3). See lib/sessionToken.js.
const getStoredToken = getSessionToken;
const setStoredToken = setSessionToken;

// Configure axios to include Authorization header if token exists
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Auth check
    try {
      const response = await axios.get(`${API}/auth/me`);
      // Authenticated
      setUser(response.data);
    } catch (error) {
      // Not authenticated
      setUser(null);
      setStoredToken(null); // Clear invalid token
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      setUser(null);
      setStoredToken(null); // Clear stored token
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const staffLogin = async (email, password) => {
    // Login attempt
    console.log('[StaffLogin] API URL:', API);
    
    try {
      const response = await axios.post(`${API}/auth/staff/login`, { email, password }, { 
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 15000
      });
      
      // Login success
      
      // Store token in localStorage (works on all devices including iOS)
      if (response.data?.session_token) {
        // Token stored
        setStoredToken(response.data.session_token);
      }
      
      setUser(response.data);
      toast.success("Logged in successfully");
      return { success: true };
    } catch (error) {
      console.error('[StaffLogin] Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Better error messages
      let errorMsg = "Login failed";
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMsg = "Cannot connect to server. Please check your internet connection and try again.";
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = "Request timed out. Please try again.";
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      return { success: false, error: errorMsg };
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, staffLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) return; // Skip if user passed from AuthCallback
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-saturn-black flex items-center justify-center">
        <div className="text-saturn-orange animate-pulse font-orbitron text-xl">
          Loading...
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/book" element={<BookingPage />} />
      <Route path="/booking-success" element={<BookingSuccessPage />} />
      <Route path="/pay-balance/:bookingId" element={<PayBalancePage />} />
      <Route path="/pay-deposit/:bookingId" element={<PayDepositPage />} />
      <Route path="/balance-success" element={<BalanceSuccessPage />} />
      <Route path="/invoice/:invoiceId" element={<InvoiceViewPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/promotion-terms" element={<PromotionTermsPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  // Inject Search Console / Bing Webmaster verification meta tags once at mount.
  // Values come from build-time env vars; if empty, no tag is rendered.
  useEffect(() => {
    const inject = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    inject('google-site-verification', process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || '');
    inject('msvalidate.01', process.env.REACT_APP_BING_SITE_VERIFICATION || '');
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-saturn-black">
          <OfflineIndicator />
          <AppRouter />
          <Toaster position="top-right" richColors />
          <ConsentBanner />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
